import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminNewsletter() {
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/newsletters").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/newsletter/subscribers").then((r) => setSubs(r.data)); }, []);

  const save = async () => {
    try {
      const payload = { title: edit.title, subject: edit.subject, body: edit.body };
      if (edit.id) await api.patch(`/admin/newsletters/${edit.id}`, payload);
      else await api.post("/admin/newsletters", payload);
      toast.success("Saved"); setEdit(null); load();
    } catch { toast.error("Failed"); }
  };
  const send = async (nid) => {
    if (!window.confirm(`Send to ${subs.length} subscriber${subs.length === 1 ? "" : "s"}? This delivers REAL emails.`)) return;
    try { const { data } = await api.post(`/admin/newsletters/${nid}/send`); toast.success(`Sent to ${data.sent_count}`); load(); }
    catch { toast.error("Send failed"); }
  };
  const remove = async (nid) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/newsletters/${nid}`); load(); };

  return (
    <div data-testid="admin-newsletter">
      <div className="flex items-end justify-between border-b border-border pb-4 mb-6">
        <div><h1 className="font-display text-4xl tracking-tight">Between The Lines</h1>
          <p className="text-sm text-muted-foreground italic mt-1">{subs.length} subscriber{subs.length === 1 ? "" : "s"} on the list.</p></div>
        <Button onClick={() => setEdit({ title: "", subject: "", body: "" })} className="rounded-sm" data-testid="new-nl"><Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />New letter</Button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="border border-dashed border-border rounded-sm p-10 text-center text-sm text-muted-foreground italic">No letters yet.</div>}
        {items.map((n) => (
          <div key={n.id} className="border border-border rounded-sm p-4 bg-card flex items-start justify-between gap-4" data-testid={`nl-${n.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-display text-xl">{n.title}</div>
                <Badge variant={n.status === "sent" ? "default" : "secondary"} className="rounded-sm capitalize">{n.status}</Badge>
                {n.sent_count > 0 && <Badge variant="outline" className="rounded-sm">{n.sent_count} sent</Badge>}
              </div>
              <div className="text-sm text-muted-foreground mt-1"><strong>Subject:</strong> {n.subject}</div>
              <div className="text-sm mt-2 text-muted-foreground line-clamp-2 whitespace-pre-line">{n.body}</div>
            </div>
            <div className="flex flex-col gap-1">
              {n.status !== "sent" && <>
                <Button variant="ghost" size="sm" onClick={() => setEdit(n)}>Edit</Button>
                <Button onClick={() => send(n.id)} size="sm" className="rounded-sm" data-testid={`send-nl-${n.id}`}><Send className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />Send</Button>
              </>}
              <Button variant="ghost" size="sm" onClick={() => remove(n.id)}><Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
            <DialogHeader><DialogTitle className="font-display text-2xl">{edit.id ? "Edit letter" : "New letter"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Internal title</Label><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="rounded-sm mt-1.5" data-testid="nl-title" /></div>
              <div><Label>Subject line</Label><Input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} className="rounded-sm mt-1.5" data-testid="nl-subject" /></div>
              <div><Label>Body (use a blank line for paragraphs)</Label><Textarea rows={12} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} className="rounded-sm mt-1.5" data-testid="nl-body" /></div>
              <div className="text-xs text-muted-foreground italic">Recipients will see "Dear &lt;first name&gt;" automatically.</div>
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} data-testid="nl-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
